import { Images } from 'assets'
import { FlexRow } from 'components'
import { SUPER_ADMIN_FLOW_ENABLED } from 'utility/super-admin-flow'
import {
    GearSix,
    ListChecks,
    SquaresFour,
    Scroll,
    Users,
    UsersThree,
    CaretDown,
    CaretUp,
    ClipboardText,
    Package,
    Ticket,
    Folders,
    Key,
    FileText,
    Bell,
    ArrowsClockwise,
    EnvelopeSimple,
    CheckCircle,
    Handshake,
} from 'phosphor-react'
import React, { useEffect, useState, useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
    SidebarPusher,
    SidebarPushable,
    Menu,
    Segment,
    Sidebar,
    Image,
} from 'semantic-ui-react'
import styled from 'styled-components'
import { HeaderBar } from './header'
import { useAuth } from 'context/user'
import { MASTER_ANSWERS_COMPANY_ID } from 'page-components/master-answers/constants'
import { useTabletAndBelowMediaQuery } from './ResponsiveMedia'
import { useReportPermissions } from 'hooks/reportPermissions'
import { useCustomReportType } from 'hooks/report'
import { getAssetPermissions } from 'utility/asset-permissions'
import { MenuSettingService } from 'utility/services/menu-setting'
import { canManageConsolidatedReports } from 'page-components/consolidated-report-email/consolidated-report-email-utils'
import { SHOW_REPORT_APPROVAL_FLOW } from 'utility/constant'
import { isRoleModelOn } from 'utility/role-model'
import { canCreateMonthlyRotation } from 'utility/monthly-rotation'
import { canApproveReports } from 'page-components/report-approval/report-approval-utils'
import { HOME_DASHBOARD_PATH, useHomeDashboardStatus } from 'utility/home-dashboard'
import { CRM_BASE_PATH, canViewCrm } from 'utility/crm/permissions'
import {
    AUDITOR_SUMMARY_PATH,
    AUDITOR_SUMMARY_ROUTE,
    canViewAuditorSummary,
} from 'page-components/report/auditor-summary/constants'
import {
    PROCESS_HEALTH_PATH,
    PROCESS_HEALTH_ROUTE,
    canViewProcessHealthReport,
} from 'page-components/report/process-health/constants'

// Temporary flag kept for quick toggling; disabled for now.
const SHOW_ASSET_MANAGEMENT_IN_SIDEBAR = false

const isReportParentMenu = menu => {
    const key = String(menu?.MenuKey || '').toLowerCase()
    const title = String(menu?.Title || '').toLowerCase()
    if (
        key === 'menu.report' ||
        key === 'report' ||
        key === 'menu.reports' ||
        title === 'report'
    ) {
        return true
    }
    return (menu?.Children || []).some(child => {
        const route = String(child?.Route || '')
        return (
            route === '/report' ||
            route === '/due-templates' ||
            route === '/checkin' ||
            route === '/combine-template' ||
            route === '/cumulative-report'
        )
    })
}

// Company 34930 keeps the pre-merge hardcoded sidebar menus.
const LEGACY_SIDEBAR_COMPANY_ID = 34930

const isClaimPortalRoute = pathname =>
    pathname.startsWith('/app/customer-defect') ||
    pathname === '/app/customer-defect-report' ||
    pathname === '/app/notification-recipients'

const SidebarContainer = styled.div`
    .pusher {
        overflow-y: hidden;
        height: 100vh !important;
        transform: translate3d(0px, 0, 0) !important;
        margin-left: ${({ isVisible, isMobile }) =>
        isVisible && !isMobile ? '160px' : '0px'};
        transition: margin-left 0.3s ease;
    }
    .menu-list {
        width: 160px !important;
        border: 0px !important;
        border-radius: 0px;
    }
    .sidebar-menu {
        width: 160px !important;
        border: 0px !important;
        border-radius: 0px;
        margin: 0px;
    }
    .ui.sidebar.menu-list {
        display: flex !important;
        flex-direction: column !important;
        overflow: hidden !important;
        height: 100vh;
        max-height: 100vh;
    }
    .sidebar-nav-scroll {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
        -webkit-overflow-scrolling: touch;
    }
    .ui.sidebar {
        visibility: unset;
    }
    .logo {
        width: 33px !important;
    }
    .menu-header {
        height: 55px;
        flex-shrink: 0;
    }
    .right-container {
        margin-top: 55px;
        height: 94vh;
        overflow-y: auto;
        overflow-x: auto;
    }
    .segment {
        border-radius: 0px !important;
        border: 0px;
    }
    /* 2026-10-17 home dashboard redesign: indigo sidebar (mockup B) for every company */
    .menu-list.ui.inverted.menu {
        background-color: #1b2587 !important;
    }
    .sub-menu {
        padding-left: 20px;
        background-color: #161f73;
    }
    .menu-list.ui.vertical.menu .sidebar-nav-scroll > .item {
        padding: 0.75em 0.875em !important;
    }
    .menu-list.ui.vertical.menu .item > a {
        display: block;
        width: 100%;
        color: rgba(255, 255, 255, 0.9);
    }
    .menu-list.ui.vertical.menu .item > a:hover {
        color: #fff;
    }
    .sidebar-menu-entry {
        display: block;
        width: 100%;
    }
    .menu-list.ui.vertical.menu .item .align-items-center > span.ms-3,
    .menu-list.ui.vertical.menu .item .align-items-center > span.ms-2 {
        flex: 1;
        min-width: 0;
        line-height: 1.25;
    }
    .sidebar-submenu-toggle {
        cursor: pointer;
        width: 100%;
        align-items: flex-start !important;
    }
    .sidebar-submenu-toggle > svg {
        flex-shrink: 0;
        margin-top: 1px;
    }
    .dropdown-arrow {
        margin-left: auto;
        flex-shrink: 0;
        align-self: center;
    }
    .menu-item-expanded {
        background-color: #161f73 !important;
    }
    .menu-item-active {
        background-color: #424a9b !important; /* lighter highlight for the active menu item */
    }
`

export const AppSidebar = ({ children }) => {
    const isMobile = useTabletAndBelowMediaQuery()
    const [visible, setVisible] = useState(!isMobile)
    const [reportOpen, setReportOpen] = useState(false)
    const [claimPortalOpen, setClaimPortalOpen] = useState(false)
    const [expandedMenus, setExpandedMenus] = useState({})
    const location = useLocation()
    const navigate = useNavigate()
    const { permissions } = useReportPermissions()
    const { reportName: cumulativeReportName } = useCustomReportType(3) // 3 is the ReportId for Cumulative Report
    const { user } = useAuth()
    const assetPermissions = getAssetPermissions(user)
    // Report Approvals: global flag, or (department role model companies) admins and named approvers
    const showReportApprovals =
        SHOW_REPORT_APPROVAL_FLOW ||
        (isRoleModelOn(user) && canApproveReports(user))
    const [sidebarMenus, setSidebarMenus] = useState([])
    const useLegacySidebar = Number(user?.CompanyId) === LEGACY_SIDEBAR_COMPANY_ID
    const useFranchisingSidebar =
        !useLegacySidebar && Number(user?.DashboardStyle?.DashboardId) === 2
    const useHardcodedSidebar = useLegacySidebar || useFranchisingSidebar
    // New home dashboard item: only for companies that switched it on; never in the hard-coded
    // Cintas / franchise menus (isHomeDashboardEligible excludes both).
    const homeStatus = useHomeDashboardStatus(user)
    const showHomeMenuItem =
        !useHardcodedSidebar && homeStatus?.available === true && homeStatus.layout === 'home'

    useEffect(() => {
        if (useHardcodedSidebar) {
            setSidebarMenus([])
            return
        }

        const fetchMenus = async () => {
            try {
                const result = await MenuSettingService.list({ onlyActive: true, onlyVisible: true })
                setSidebarMenus(result || [])
            } catch (error) {
                console.error("Error fetching sidebar menus:", error)
            }
        }
        fetchMenus()
    }, [useHardcodedSidebar])

    const filteredMenus = useMemo(() => {
        if (useHardcodedSidebar || !sidebarMenus || sidebarMenus.length === 0) return [];

        const userMenuss = user?.Menus || []
        const permissionKeys = user?.PermissionKeys || [];
        const lowerKeys = permissionKeys.map(k => k.toLowerCase());

        // Extract all MenuKeys recursively from userMenuss
        const extractUserMenuKeys = (menusList) => {
            const keys = new Set();
            const traverse = (items) => {
                if (!Array.isArray(items)) return;
                items.forEach(item => {
                    if (item?.MenuKey) {
                        const k = item.MenuKey.toLowerCase();
                        keys.add(k);
                        if (k.startsWith('menu.')) {
                            keys.add(k.slice(5));
                        } else {
                            keys.add(`menu.${k}`);
                        }
                    }
                    if (item?.Children && Array.isArray(item.Children)) {
                        traverse(item.Children);
                    }
                });
            };
            traverse(menusList);
            return keys;
        };

        const userMenuKeysSet = extractUserMenuKeys(userMenuss);

        // 1. Build a nested tree from the flat list of menu items
        const menuMap = {};
        const roots = [];

        sidebarMenus.forEach((item) => {
            menuMap[item.MenuId] = { ...item, Children: [] };
        });

        sidebarMenus.forEach((item) => {
            if (item.ParentMenuId && menuMap[item.ParentMenuId]) {
                menuMap[item.ParentMenuId].Children.push(menuMap[item.MenuId]);
            } else {
                roots.push(menuMap[item.MenuId]);
            }
        });

        // 2. Filter the tree nodes recursively
        const filterNode = (menu) => {
            const hasChildren = menu.Children && menu.Children.length > 0;

            let filteredChildren = [];
            if (hasChildren) {
                filteredChildren = menu.Children
                    .map(filterNode)
                    .filter(Boolean);
            }

            // Check permissions: all menus (root and submenu) check for MenuKey.view or direct match
            const requiredKey = `${menu.MenuKey}.view`.toLowerCase();
            const directKey = menu.MenuKey.toLowerCase();

            let withoutMenuRequiredKey = '';
            let withoutMenuDirectKey = '';
            if (menu.MenuKey.toLowerCase().startsWith('menu.')) {
                const stripped = menu.MenuKey.slice(5);
                withoutMenuRequiredKey = `${stripped}.view`.toLowerCase();
                withoutMenuDirectKey = stripped.toLowerCase();
            }

            const matchesUserMenu =
                userMenuKeysSet.has(directKey) ||
                (withoutMenuDirectKey && userMenuKeysSet.has(withoutMenuDirectKey));

            const isAllowed =
                matchesUserMenu ||
                lowerKeys.includes(requiredKey) ||
                lowerKeys.includes(directKey) ||
                (withoutMenuRequiredKey && lowerKeys.includes(withoutMenuRequiredKey)) ||
                (withoutMenuDirectKey && lowerKeys.includes(withoutMenuDirectKey)) ||
                (hasChildren && filteredChildren.length > 0);

            if (!isAllowed) {
                return null;
            }

            if (hasChildren) {
                return {
                    ...menu,
                    Children: filteredChildren,
                };
            }

            return menu;
        };

        return roots.map(filterNode).filter(Boolean).map(menu => {
            const visibleChildren = canViewProcessHealthReport(user)
                ? menu.Children || []
                : (menu.Children || []).filter(
                      child =>
                          child.Route !== PROCESS_HEALTH_ROUTE &&
                          child.MenuKey !== 'menu.reports.processHealth',
                  )
            const menuForReport =
                visibleChildren === menu.Children
                    ? menu
                    : { ...menu, Children: visibleChildren }
            if (!isReportParentMenu(menuForReport)) return menuForReport
            const children = menuForReport.Children || []
            const extras = []
            if (
                canViewAuditorSummary(user) &&
                !children.some(
                    child =>
                        child.Route === AUDITOR_SUMMARY_ROUTE ||
                        child.MenuKey === 'menu.reports.auditorSummary',
                )
            ) {
                extras.push({
                    MenuId: 'auditor-summary',
                    ParentMenuId: menu.MenuId,
                    MenuKey: 'menu.reports.auditorSummary',
                    Title: 'Resumen de evaluaciones',
                    Route: AUDITOR_SUMMARY_ROUTE,
                    IconKey: 'file-text',
                    SortOrder: 999,
                    Children: [],
                })
            }
            if (
                canViewProcessHealthReport(user) &&
                !children.some(
                    child =>
                        child.Route === PROCESS_HEALTH_ROUTE ||
                        child.MenuKey === 'menu.reports.processHealth',
                )
            ) {
                const combine = children.find(
                    child => child.Route === '/combine-template',
                )
                extras.push({
                    MenuId: 'process-health',
                    ParentMenuId: menu.MenuId,
                    MenuKey: 'menu.reports.processHealth',
                    Title: 'Process Health Check',
                    Route: PROCESS_HEALTH_ROUTE,
                    IconKey: 'file-text',
                    SortOrder: combine
                        ? Number(combine.SortOrder || 0) + 0.5
                        : 40,
                    Children: [],
                })
            }
            if (!extras.length) return menuForReport
            return {
                ...menuForReport,
                Children: [...children, ...extras],
            }
        })
    }, [useHardcodedSidebar, sidebarMenus, user, user?.PermissionKeys, user?.Menus, user?.Capabilities]);

    useEffect(() => {
        if (isMobile) {
            setVisible(false)
        }
    }, [isMobile])

    useEffect(() => {
        if (isClaimPortalRoute(location.pathname)) {
            setClaimPortalOpen(true)
        }
    }, [location.pathname])

    useEffect(() => {
        if (!useHardcodedSidebar && filteredMenus.length > 0) {
            const initialExpanded = {}
            filteredMenus.forEach(menu => {
                if (menu.Children && menu.Children.some(child => location.pathname === `/app${child.Route}`)) {
                    initialExpanded[menu.MenuId] = true;
                }
            });
            setExpandedMenus(initialExpanded);
        }
    }, [useHardcodedSidebar, filteredMenus, location.pathname]);

    const onHide = () => {
        if (isMobile) {
            setVisible(false)
        }
    }

    const handleNavigation = () => {
        if (isMobile) {
            setVisible(false)
        }
    }

    const toggleReport = () => {
        setReportOpen(prev => !prev)
    }

    useEffect(() => {
        if (
            location.pathname === AUDITOR_SUMMARY_PATH ||
            location.pathname === PROCESS_HEALTH_PATH
        ) {
            setReportOpen(true)
        }
    }, [location.pathname])

    const handleClaimPortalMenuClick = () => {
        if (claimPortalOpen) {
            setClaimPortalOpen(false)
            return
        }
        setClaimPortalOpen(true)
        if (!isClaimPortalRoute(location.pathname)) {
            navigate('/app/customer-defect')
        }
        handleNavigation()
    }

    const toggleMenu = (e, menuId) => {
        e.preventDefault();
        e.stopPropagation();
        setExpandedMenus(prev => ({
            ...prev,
            [menuId]: !prev[menuId],
        }));
    };

    const renderTicketsMenuItem = () => (
        <Menu.Item
            name="tickets"
            onClick={handleNavigation}
            className={
                location.pathname === '/app/tickets' ||
                location.pathname.startsWith('/app/ticket/')
                    ? 'menu-item-active'
                    : ''
            }
        >
            <Link to="/app/tickets">
                <FlexRow className="align-items-center">
                    <Ticket size={24} />
                    <span className="ms-3">Tickets</span>
                </FlexRow>
            </Link>
        </Menu.Item>
    )

    const renderConsolidatedReportEmailMenuItem = () => (
        <Menu.Item
            name="consolidated-report-email"
            onClick={handleNavigation}
            className={
                location.pathname.startsWith('/app/consolidated-report-email')
                    ? 'menu-item-active'
                    : ''
            }
        >
            <Link to="/app/consolidated-report-email">
                <FlexRow className="align-items-center">
                    <EnvelopeSimple size={24} />
                    <span className="ms-3">Schedule Reports</span>
                </FlexRow>
            </Link>
        </Menu.Item>
    );

    const renderReportApprovalsMenuItem = () => (
        <Menu.Item
            name="report-approvals"
            onClick={handleNavigation}
            className={
                location.pathname.startsWith('/app/report-approvals')
                    ? 'menu-item-active'
                    : ''
            }
        >
            <Link to="/app/report-approvals">
                <FlexRow className="align-items-center">
                    <CheckCircle size={24} />
                    <span className="ms-3">Report Approvals</span>
                </FlexRow>
            </Link>
        </Menu.Item>
    );

    const getIcon = (iconKey, size = 24) => {
        const ICON_MAP = {
            templates: Scroll,
            report: GearSix,
            'report-item': GearSix,
            customer: Users,
            group: UsersThree,
            task: ListChecks,
            'action-plan': ClipboardText,
            settings: GearSix,
            support: Ticket,
            invoice: FileText,
            'invoice-item': FileText,
            'file-text': FileText,
            asset: Package,
            key: Key,
            bell: Bell,
            package: Package,
            arrowsClockwise: ArrowsClockwise,
            'check-circle': CheckCircle,
        };
        const IconComponent = ICON_MAP[iconKey] || SquaresFour;
        return <IconComponent size={size} />;
    };

    const renderCrmMenuItem = () => {
        if (!canViewCrm(user)) return null
        return (
            <Menu.Item
                name="crm"
                onClick={handleNavigation}
                className={
                    location.pathname.startsWith(CRM_BASE_PATH)
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to={CRM_BASE_PATH}>
                    <FlexRow className="align-items-center">
                        <Handshake size={24} />
                        <span className="ms-3">CRM</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
        )
    }

    const renderAuditorSummaryMenuItem = () => {
        if (!canViewAuditorSummary(user)) return null
        return (
            <Menu.Item
                name="auditor-summary"
                onClick={handleNavigation}
                className={
                    location.pathname === AUDITOR_SUMMARY_PATH
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to={AUDITOR_SUMMARY_PATH}>
                    <FlexRow className="align-items-center">
                        <FileText size={20} />
                        <span className="ms-2">Resumen de evaluaciones</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
        )
    }

    const renderProcessHealthMenuItem = () => {
        if (!canViewProcessHealthReport(user)) return null
        return (
            <Menu.Item
                name="process-health"
                onClick={handleNavigation}
                className={
                    location.pathname === PROCESS_HEALTH_PATH
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to={PROCESS_HEALTH_PATH}>
                    <FlexRow className="align-items-center">
                        <FileText size={20} />
                        <span className="ms-2">Process Health Check</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
        )
    }

    const renderDynamicMenus = (menus) => {
        if (!menus) return null;

        const sortedMenus = [...menus].sort((a, b) => a.SortOrder - b.SortOrder);

        return sortedMenus.map(menu => {
            const hasChildren = menu.Children && menu.Children.length > 0;
            const isExpanded = expandedMenus[menu.MenuId];
            let rawRoute = String(menu?.Route || '').trim()
            if (rawRoute && !rawRoute.startsWith('/')) rawRoute = `/${rawRoute}`
            const menuKey = String(menu?.MenuKey || '').toLowerCase()
            const menuTitle = String(menu?.Title || '').trim().toLowerCase()
            const isSettingsRoot =
                !menu?.ParentMenuId &&
                (rawRoute === '/settings' ||
                    rawRoute === '/settings/user' ||
                    ((menuTitle === 'settings' ||
                        menuKey === 'settings' ||
                        menuKey === 'menu.settings' ||
                        menuKey.endsWith('.settings')) &&
                        rawRoute.startsWith('/settings')))
            const route = isSettingsRoot ? '/app/settings' : `/app${rawRoute}`

            if (hasChildren) {
                const isChildActive = menu.Children.some(child => location.pathname === `/app${child.Route}`);

                return (
                    <Menu.Item
                        key={menu.MenuId}
                        name={menu.MenuKey}
                        className={isExpanded || isChildActive ? 'menu-item-expanded' : ''}
                    >
                        <FlexRow className="align-items-center" onClick={(e) => toggleMenu(e, menu.MenuId)} style={{ cursor: 'pointer' }}>
                            {getIcon(menu.IconKey, 24)}
                            <span className="ms-3">{menu.Title}</span>
                            <span className="dropdown-arrow">
                                {isExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
                            </span>
                        </FlexRow>
                        {isExpanded && (
                            <Menu.Menu className="sub-menu">
                                {renderDynamicMenus(menu.Children)}
                            </Menu.Menu>
                        )}
                    </Menu.Item>
                );
            }

            let displayTitle = menu.Title;
            if (menu.MenuKey === 'menu.reports.cumulativeReport' && cumulativeReportName) {
                displayTitle = cumulativeReportName;
            }

            const isSettingsRoute = route === '/app/settings'
            const isActive = isSettingsRoute
                ? location.pathname === '/app/settings' ||
                  location.pathname.startsWith('/app/settings/')
                : location.pathname === route

            return (
                <Menu.Item
                    key={menu.MenuId}
                    name={menu.MenuKey}
                    onClick={handleNavigation}
                    className={isActive ? 'menu-item-active' : ''}
                >
                    <Link to={route}>
                        <FlexRow className="align-items-center">
                            {getIcon(menu.IconKey, menu.ParentMenuId ? 20 : 24)}
                            <span className={menu.ParentMenuId ? "ms-2" : "ms-3"}>
                                {displayTitle}
                            </span>
                        </FlexRow>
                    </Link>
                </Menu.Item>
            );
        });
    };

    const renderLegacySidebarMenus = () => (
        <>
            {renderSuperAdminMenuItem()}
            {user?.DashboardStyle?.DashboardId === 2 && (
                <Menu.Item
                    name="dashboard"
                    onClick={handleNavigation}
                    className={
                        location.pathname.startsWith('/app/dashboard')
                            ? 'menu-item-active'
                            : ''
                    }
                >
                    <Link to="/app/dashboard/groups">
                        <FlexRow className="align-items-center">
                            <SquaresFour size={24} />
                            <span className="ms-3">Dashboard</span>
                        </FlexRow>
                    </Link>
                </Menu.Item>
            )}
            <Menu.Item name="templates" onClick={handleNavigation}>
                <Link to="/app/templates">
                    <FlexRow className="align-items-center">
                        <Scroll size={24} />
                        <span className="ms-3">WK Audits Templates</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            <Menu.Item
                name="daily-audit-templates"
                onClick={handleNavigation}
                className={
                    location.pathname === '/app/templates/daily-audit'
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to="/app/templates/daily-audit">
                    <FlexRow className="align-items-center">
                        <Scroll size={24} />
                        <span className="ms-3">
                            Large Press Daily Audit Templates
                        </span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            <Menu.Item
                name="small-daily-audit-template"
                onClick={handleNavigation}
                className={
                    location.pathname === '/app/templates/small-daily-audit'
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to="/app/templates/small-daily-audit">
                    <FlexRow className="align-items-center">
                        <Scroll size={24} />
                        <span className="ms-3">
                            Small Press Daily Audit Templates
                        </span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            <Menu.Item
                name="mat-finish-templates"
                onClick={handleNavigation}
                className={
                    location.pathname === '/app/templates/mat-finish'
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to="/app/templates/mat-finish">
                    <FlexRow className="align-items-center">
                        <Scroll size={24} />
                        <span className="ms-3">Finish Mat</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            <Menu.Item
                name="hundred-inspection-templates"
                onClick={handleNavigation}
                className={
                    location.pathname === '/app/templates/hundred-inspection'
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to="/app/templates/hundred-inspection">
                    <FlexRow className="align-items-center">
                        <Scroll size={24} />
                        <span className="ms-3">100% Inspection</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            <Menu.Item
                name="tuft-lock-templates"
                onClick={handleNavigation}
                className={
                    location.pathname === '/app/templates/tuft-lock'
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to="/app/templates/tuft-lock">
                    <FlexRow className="align-items-center">
                        <Scroll size={24} />
                        <span className="ms-3">Tuft Lock</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            <Menu.Item
                name="platen-audit-templates"
                onClick={handleNavigation}
                className={
                    location.pathname === '/app/templates/platen-audit'
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to="/app/templates/platen-audit">
                    <FlexRow className="align-items-center">
                        <Scroll size={24} />
                        <span className="ms-3">Platen Audit</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            <Menu.Item
                name="daily-defect-report-templates"
                onClick={handleNavigation}
                className={
                    location.pathname === '/app/templates/daily-defect-report'
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to="/app/templates/daily-defect-report">
                    <FlexRow className="align-items-center">
                        <Scroll size={24} />
                        <span className="ms-3">Mimaki Production</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            <Menu.Item
                name="daily-scrap-rework-templates"
                onClick={handleNavigation}
                className={
                    location.pathname === '/app/templates/daily-scrap-rework'
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to="/app/templates/daily-scrap-rework">
                    <FlexRow className="align-items-center">
                        <Scroll size={24} />
                        <span className="ms-3">Daily Production Scrap/Rework</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            <Menu.Item
                name="raw-material-qc-inspection-templates"
                onClick={handleNavigation}
                className={
                    location.pathname === '/app/templates/raw-material-qc-inspection'
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to="/app/templates/raw-material-qc-inspection">
                    <FlexRow className="align-items-center">
                        <Scroll size={24} />
                        <span className="ms-3">Raw Material QC Inspection</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            <Menu.Item
                name="macola-scrap-codes"
                onClick={handleNavigation}
                className={
                    location.pathname === '/app/templates/macola-scrap-codes'
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to="/app/templates/macola-scrap-codes">
                    <FlexRow className="align-items-center">
                        <Scroll size={24} />
                        <span className="ms-3">Macola Scrap Codes</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            <Menu.Item
                name="customer-claim-portal"
                className={claimPortalOpen ? 'menu-item-expanded' : ''}
            >
                <div
                    className="sidebar-menu-entry"
                    onClick={handleClaimPortalMenuClick}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleClaimPortalMenuClick()
                        }
                    }}
                    role="button"
                    tabIndex={0}
                >
                    <FlexRow className="align-items-center sidebar-submenu-toggle">
                        <Scroll size={24} />
                        <span className="ms-3">
                            Customer Service Claim Portal
                        </span>
                        <span className="dropdown-arrow">
                            {claimPortalOpen ? (
                                <CaretUp size={16} />
                            ) : (
                                <CaretDown size={16} />
                            )}
                        </span>
                    </FlexRow>
                </div>
                {claimPortalOpen && (
                    <Menu.Menu className="sub-menu">
                        <Menu.Item
                            onClick={handleNavigation}
                            className={
                                (location.pathname === '/app/customer-defect' ||
                                    location.pathname.startsWith(
                                        '/app/customer-defect/',
                                    )) &&
                                    !location.pathname.startsWith(
                                        '/app/customer-defect-report',
                                    )
                                    ? 'menu-item-active'
                                    : ''
                            }
                        >
                            <Link to="/app/customer-defect">
                                <FlexRow className="align-items-center">
                                    <Scroll size={20} />
                                    <span className="ms-2">New Claim</span>
                                </FlexRow>
                            </Link>
                        </Menu.Item>
                        <Menu.Item
                            onClick={handleNavigation}
                            className={
                                location.pathname ===
                                    '/app/customer-defect-report'
                                    ? 'menu-item-active'
                                    : ''
                            }
                        >
                            <Link to="/app/customer-defect-report">
                                <FlexRow className="align-items-center">
                                    <Scroll size={20} />
                                    <span className="ms-2">All Claims</span>
                                </FlexRow>
                            </Link>
                        </Menu.Item>
                        <Menu.Item
                            onClick={handleNavigation}
                            className={
                                location.pathname ===
                                    '/app/notification-recipients'
                                    ? 'menu-item-active'
                                    : ''
                            }
                        >
                            <Link to="/app/notification-recipients">
                                <FlexRow className="align-items-center">
                                    <Bell size={20} />
                                    <span className="ms-2">Phase Setup</span>
                                </FlexRow>
                            </Link>
                        </Menu.Item>
                    </Menu.Menu>
                )}
            </Menu.Item>
            {canCreateMonthlyRotation(user) && (
                <Menu.Item
                    name="monthly-rotation"
                    onClick={handleNavigation}
                    className={
                        location.pathname === '/app/monthly-rotation'
                            ? 'menu-item-active'
                            : ''
                    }
                >
                    <Link to="/app/monthly-rotation">
                        <FlexRow className="align-items-center">
                            <ArrowsClockwise size={24} />
                            <span className="ms-3">Monthly Rotation</span>
                        </FlexRow>
                    </Link>
                </Menu.Item>
            )}
            <Menu.Item name="group" onClick={handleNavigation}>
                <Link to="/app/group">
                    <FlexRow className="align-items-center">
                        <UsersThree size={24} />
                        <span className="ms-3">Group</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            {canManageConsolidatedReports(user) &&
                renderConsolidatedReportEmailMenuItem()}
            {showReportApprovals && renderReportApprovalsMenuItem()}
            <Menu.Item name="task" onClick={handleNavigation}>
                <Link to="/app/task">
                    <FlexRow className="align-items-center">
                        <ListChecks size={24} />
                        <span className="ms-3">Audit Report</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            {Number(user?.CompanyId) === MASTER_ANSWERS_COMPANY_ID && (
                <Menu.Item
                    name="master-answers"
                    onClick={handleNavigation}
                    className={
                        location.pathname.startsWith('/app/master-answers')
                            ? 'menu-item-active'
                            : ''
                    }
                >
                    <Link to="/app/master-answers">
                        <FlexRow className="align-items-center">
                            <Folders size={24} />
                            <span className="ms-3">Master Answers</span>
                        </FlexRow>
                    </Link>
                </Menu.Item>
            )}
            {SHOW_ASSET_MANAGEMENT_IN_SIDEBAR &&
                assetPermissions.canViewAssetModule && (
                    <Menu.Item
                        name="asset-management"
                        onClick={handleNavigation}
                        className={
                            location.pathname.startsWith(
                                '/app/asset-management',
                            )
                                ? 'menu-item-active'
                                : ''
                        }
                    >
                        <Link to="/app/asset-management">
                            <FlexRow className="align-items-center">
                                <Package size={24} />
                                <span className="ms-3">Asset Management</span>
                            </FlexRow>
                        </Link>
                    </Menu.Item>
                )}
            {renderTicketsMenuItem()}
            <Menu.Item
                name="settings"
                onClick={handleNavigation}
                className={
                    location.pathname.startsWith('/app/settings')
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to="/app/settings">
                    <FlexRow className="align-items-center">
                        <GearSix size={24} />
                        <span className="ms-3">Settings</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            <Menu.Item
                name="support-tickets"
                onClick={handleNavigation}
                className={
                    location.pathname === '/app/support-tickets'
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to="/app/support-tickets">
                    <FlexRow className="align-items-center">
                        <Ticket size={24} />
                        <span className="ms-3">Raise a Support Ticket</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
        </>
    )

    const renderDefaultFallbackMenus = (showMenuAdministration = true) => (
        <>
            {user?.DashboardStyle?.DashboardId === 2 && (
                <Menu.Item
                    name="dashboard"
                    onClick={handleNavigation}
                    className={
                        location.pathname.startsWith('/app/dashboard')
                            ? 'menu-item-active'
                            : ''
                    }
                >
                    <Link to="/app/dashboard/groups">
                        <FlexRow className="align-items-center">
                            <SquaresFour size={24} />
                            <span className="ms-3">Dashboard</span>
                        </FlexRow>
                    </Link>
                </Menu.Item>
            )}
            <Menu.Item name="templates" onClick={handleNavigation}>
                <Link to="/app/templates">
                    <FlexRow className="align-items-center">
                        <Scroll size={24} />
                        <span className="ms-3">Templates List</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            <Menu.Item
                name="report"
                className={reportOpen ? 'menu-item-expanded' : ''}
            >
                <FlexRow className="align-items-center">
                    <GearSix size={24} />
                    <span className="ms-3">Report</span>
                    <span
                        className="dropdown-arrow"
                        onClick={toggleReport}
                    >
                        {reportOpen ? (
                            <CaretUp size={16} />
                        ) : (
                            <CaretDown size={16} />
                        )}
                    </span>
                </FlexRow>
                {reportOpen && (
                    <Menu.Menu className="sub-menu">
                        <Menu.Item
                            onClick={handleNavigation}
                            className={
                                location.pathname === '/app/report'
                                    ? 'menu-item-active'
                                    : ''
                            }
                        >
                            <Link to="/app/report">
                                <FlexRow className="align-items-center">
                                    <GearSix size={20} />
                                    <span className="ms-2">Inspection</span>
                                </FlexRow>
                            </Link>
                        </Menu.Item>
                        <Menu.Item
                            onClick={handleNavigation}
                            className={
                                location.pathname === '/app/checkin'
                                    ? 'menu-item-active'
                                    : ''
                            }
                        >
                            <Link to="/app/checkin">
                                <FlexRow className="align-items-center">
                                    <GearSix size={20} />
                                    <span className="ms-2">Check-In</span>
                                </FlexRow>
                            </Link>
                        </Menu.Item>
                        <Menu.Item
                            onClick={handleNavigation}
                            className={
                                location.pathname === '/app/combine-template'
                                    ? 'menu-item-active'
                                    : ''
                            }
                        >
                            <Link to="/app/combine-template">
                                <FlexRow className="align-items-center">
                                    <GearSix size={20} />
                                    <span className="ms-2">Combine Template</span>
                                </FlexRow>
                            </Link>
                        </Menu.Item>
                        {renderProcessHealthMenuItem()}
                        {permissions.hasCumulativeReport && (
                            <Menu.Item
                                onClick={handleNavigation}
                                className={
                                    location.pathname ===
                                        '/app/cumulative-report'
                                        ? 'menu-item-active'
                                        : ''
                                }
                            >
                                <Link to="/app/cumulative-report">
                                    <FlexRow className="align-items-center">
                                        <GearSix size={20} />
                                        <span className="ms-2">
                                            {cumulativeReportName ||
                                                'Cumulative Report'}
                                        </span>
                                    </FlexRow>
                                </Link>
                            </Menu.Item>
                        )}
                        <Menu.Item
                            onClick={handleNavigation}
                            className={
                                location.pathname === '/app/due-templates'
                                    ? 'menu-item-active'
                                    : ''
                            }
                        >
                            <Link to="/app/due-templates">
                                <FlexRow className="align-items-center">
                                    <GearSix size={20} />
                                    <span className="ms-2">Due Templates</span>
                                </FlexRow>
                            </Link>
                        </Menu.Item>
                        {renderAuditorSummaryMenuItem()}
                    </Menu.Menu>
                )}
            </Menu.Item>
            <Menu.Item name="customer" onClick={handleNavigation}>
                <Link to="/app/customer">
                    <FlexRow className="align-items-center">
                        <Users size={24} />
                        <span className="ms-3">Customer</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            <Menu.Item name="group" onClick={handleNavigation}>
                <Link to="/app/group">
                    <FlexRow className="align-items-center">
                        <UsersThree size={24} />
                        <span className="ms-3">Group</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            {canManageConsolidatedReports(user) &&
                renderConsolidatedReportEmailMenuItem()}
            {showReportApprovals && renderReportApprovalsMenuItem()}
            <Menu.Item name="task" onClick={handleNavigation}>
                <Link to="/app/task">
                    <FlexRow className="align-items-center">
                        <ListChecks size={24} />
                        <span className="ms-3">Task</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            <Menu.Item name="action-plan" onClick={handleNavigation}>
                <Link to="/app/action-plan">
                    <FlexRow className="align-items-center">
                        <ClipboardText size={24} />
                        <span className="ms-3">Action Plan</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            {renderTicketsMenuItem()}
            {SHOW_ASSET_MANAGEMENT_IN_SIDEBAR &&
                assetPermissions.canViewAssetModule && (
                    <Menu.Item
                        name="asset-management"
                        onClick={handleNavigation}
                        className={
                            location.pathname.startsWith('/app/asset-management')
                                ? 'menu-item-active'
                                : ''
                        }
                    >
                        <Link to="/app/asset-management">
                            <FlexRow className="align-items-center">
                                <Package size={24} />
                                <span className="ms-3">Asset Management</span>
                            </FlexRow>
                        </Link>
                    </Menu.Item>
                )}
            <Menu.Item
                name="settings"
                onClick={handleNavigation}
                className={
                    location.pathname.startsWith('/app/settings')
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to="/app/settings">
                    <FlexRow className="align-items-center">
                        <GearSix size={24} />
                        <span className="ms-3">Settings</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
            {showMenuAdministration && (
                <>
                    <Menu.Item
                        name="menu-settings"
                        onClick={handleNavigation}
                        className={
                            location.pathname === '/app/menu-settings'
                                ? 'menu-item-active'
                                : ''
                        }
                    >
                        <Link to="/app/menu-settings">
                            <FlexRow className="align-items-center">
                                <GearSix size={24} />
                                <span className="ms-3">Menu Settings</span>
                            </FlexRow>
                        </Link>
                    </Menu.Item>
                    <Menu.Item
                        name="Permission"
                        onClick={handleNavigation}
                        className={
                            location.pathname === '/app/permission'
                                ? 'menu-item-active'
                                : ''
                        }
                    >
                        <Link to="/app/permission">
                            <FlexRow className="align-items-center">
                                <Key size={24} />
                                <span className="ms-3">Permission</span>
                            </FlexRow>
                        </Link>
                    </Menu.Item>
                </>
            )}
            <Menu.Item
                name="support-tickets"
                onClick={handleNavigation}
                className={
                    location.pathname === '/app/support-tickets'
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to="/app/support-tickets">
                    <FlexRow className="align-items-center">
                        <Ticket size={24} />
                        <span className="ms-3">Raise a Support Ticket</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
        </>
    )

    const renderSuperAdminMenuItem = () => {
        if (!SUPER_ADMIN_FLOW_ENABLED) return null
        return (
            <Menu.Item
                name="super-admin"
                onClick={handleNavigation}
                className={
                    location.pathname.startsWith('/app/super-admin')
                        ? 'menu-item-active'
                        : ''
                }
            >
                <Link to="/app/super-admin">
                    <FlexRow className="align-items-center">
                        <SquaresFour size={24} />
                        <span className="ms-3">Super Admin</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
        )
    }

    const renderHomeMenuItem = () =>
        showHomeMenuItem ? (
            <Menu.Item
                name="home"
                onClick={handleNavigation}
                className={location.pathname === HOME_DASHBOARD_PATH ? 'menu-item-active' : ''}
            >
                <Link to={HOME_DASHBOARD_PATH}>
                    <FlexRow className="align-items-center">
                        <SquaresFour size={24} />
                        <span className="ms-3">Home</span>
                    </FlexRow>
                </Link>
            </Menu.Item>
        ) : null

    const renderSidebarMenus = () => {
        if (useLegacySidebar) {
            return renderLegacySidebarMenus()
        }

        if (useFranchisingSidebar) {
            return renderDefaultFallbackMenus(false)
        }

        if (filteredMenus && filteredMenus.length > 0) {
            return (
                <>
                    {user?.DashboardStyle?.DashboardId === 2 && !filteredMenus.some(m => m.Route === '/dashboard' || m.Route === '/dashboard/groups') && (
                        <Menu.Item
                            name="dashboard"
                            onClick={handleNavigation}
                            className={
                                location.pathname.startsWith('/app/dashboard')
                                    ? 'menu-item-active'
                                    : ''
                            }
                        >
                            <Link to="/app/dashboard/groups">
                                <FlexRow className="align-items-center">
                                    <SquaresFour size={24} />
                                    <span className="ms-3">Dashboard</span>
                                </FlexRow>
                            </Link>
                        </Menu.Item>
                    )}
                    {renderSuperAdminMenuItem()}
                    {renderHomeMenuItem()}
                    {renderDynamicMenus(filteredMenus)}
                    {!filteredMenus.some(menu => {
                        const route = String(menu?.Route || '').toLowerCase()
                        const title = String(menu?.Title || '').toLowerCase()
                        return route === '/tickets' || title === 'tickets'
                    }) && renderTicketsMenuItem()}
                    {renderCrmMenuItem()}
                    {canManageConsolidatedReports(user) &&
                        renderConsolidatedReportEmailMenuItem()}
                    {showReportApprovals &&
                        renderReportApprovalsMenuItem()}
                </>
            )
        }

        return (
            <>
                {renderSuperAdminMenuItem()}
                {renderHomeMenuItem()}
                {renderDefaultFallbackMenus()}
            </>
        )
    }

    return (
        <SidebarContainer isMobile={isMobile} isVisible={visible}>
            <SidebarPushable as={Segment} style={{ overflow: 'hidden' }}>
                <Sidebar
                    as={Menu}
                    animation={isMobile ? 'overlay' : 'slide along'}
                    direction="left"
                    inverted
                    onHide={onHide}
                    vertical
                    visible={visible}
                    width="thin"
                    className="menu-list"
                >
                    <Menu.Item header className="menu-header">
                        <FlexRow className="align-items-center">
                            <Image
                                className="logo"
                                src={Images.Logo}
                                size="small"
                            />
                            <span className="ms-3">EAuditor</span>
                        </FlexRow>
                    </Menu.Item>
                    <div className="sidebar-nav-scroll">
                        {renderSidebarMenus()}
                    </div>
                </Sidebar>
                <SidebarPusher>
                    <HeaderBar visible={visible} setVisible={setVisible} />
                    <Segment
                        id="right-container"
                        basic
                        className="right-container"
                    >
                        <div>{children}</div>
                    </Segment>
                </SidebarPusher>
            </SidebarPushable>
        </SidebarContainer>
    )
}
